import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { showToast } from "../../../core/utils/alert";
import { useSelector } from "react-redux";
import {
  selectUserRole,
  selectCurrentUser,
} from "../../../core/data/slice/authSlice";
import {
  useGenerateInvoicesForCenterMutation,
  useGenerateRegistrationInvoicesForCenterMutation,
} from "../api/invoiceApi";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import { useGetCenterByIdQuery } from "../../peoples/centers/api/centerApi";

const GenerateInvoices = () => {
  const [selectedCenterId, setSelectedCenterId] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStage, setGenerationStage] = useState<
    "idle" | "preparing" | "generating" | "finalizing"
  >("idle");
  const [generatingType, setGeneratingType] = useState<
    "none" | "course" | "registration" | "complete"
  >("none");
  const [hasCourseFees, setHasCourseFees] = useState(true);
  const [hasRegistrationFees, setHasRegistrationFees] = useState(true);

  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectCurrentUser); // Get current user data

  // API hooks
  const [generateCourseInvoices] = useGenerateInvoicesForCenterMutation();
  const [generateRegistrationInvoices] =
    useGenerateRegistrationInvoicesForCenterMutation();

  // Fetch centers based on user role
  const { getCenterState, useGetCenterById } = useCenter({
    skip: userRole !== "superadmin",
  });
  const { centers, isLoading: centersLoading } = getCenterState();

  // For centeradmin, fetch specific center data
  const centerId = currentUser?.center_id?.toString() || "";
  const isCenterAdmin = userRole === "centeradmin";
  const { data: centerData, isLoading: centerLoading } = useGetCenterById(
    isCenterAdmin ? centerId : ""
  );

  // Set the current center for centeradmin
  useEffect(() => {
    if (userRole === "centeradmin" && currentUser?.center_id) {
      const centerId = currentUser.center_id.toString();
      setSelectedCenterId(centerId);
    }
  }, [userRole, currentUser]);

  // Update fee status when centerData is loaded for centeradmin
  useEffect(() => {
    if (isCenterAdmin && centerData?.data?.center) {
      const center = centerData.data.center;

      // Check for course fees (courseFees array)
      const hasCourse = !!center.courseFees && center.courseFees.length > 0;

      // Check for registration fees (regFees array in the API response)
      const hasRegistration = !!center.regFees && center.regFees.length > 0;

      setHasCourseFees(hasCourse);
      setHasRegistrationFees(hasRegistration);

      console.log("Center data loaded for centeradmin:", {
        centerId,
        hasCourse,
        hasRegistration,
        courseFees: center.courseFees,
        regFees: center.regFees,
      });
    }
  }, [isCenterAdmin, centerData, centerId]);

  // Add navigate hook
  const navigate = useNavigate();

  const getDashboardRoute = () => {
    switch (userRole) {
      case "superadmin":
        return all_routes.adminDashboard;
      case "centeradmin":
        return all_routes.centerDashboard;
      default:
        return all_routes.adminDashboard;
    }
  };

  // Create a state for the selected center ID for the RTK Query
  const [selectedCenterForQuery, setSelectedCenterForQuery] =
    useState<string>("");

  // Use the RTK Query hook to fetch center data
  const { data: selectedCenterData, isLoading: isSelectedCenterLoading } =
    useGetCenterByIdQuery(
      { id: selectedCenterForQuery },
      { skip: !selectedCenterForQuery }
    );

  // Update fee status when selected center data is loaded
  useEffect(() => {
    if (selectedCenterData?.data?.center) {
      const center = selectedCenterData.data.center;

      // Check for course fees
      const hasCourse = !!center.courseFees && center.courseFees.length > 0;

      // Check for registration fees (using regFees property)
      const hasRegistration = !!center.regFees && center.regFees.length > 0;

      setHasCourseFees(hasCourse);
      setHasRegistrationFees(hasRegistration);

      console.log("Center data loaded from API:", {
        centerId: selectedCenterForQuery,
        hasCourse,
        hasRegistration,
        courseFees: center.courseFees,
        regFees: center.regFees,
      });
    }
  }, [selectedCenterData]);

  // Handle center selection
  const handleCenterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const centerId = e.target.value;
    setSelectedCenterId(centerId);

    // Reset fee status if no center is selected
    if (!centerId) {
      setHasCourseFees(true);
      setHasRegistrationFees(true);
      setSelectedCenterForQuery("");
      return;
    }

    // For both superadmin and centeradmin, trigger the RTK Query
    setSelectedCenterForQuery(centerId);
  };

  // Handle generate invoices
  const handleGenerateInvoices = async () => {
    if (!selectedCenterId) {
      showToast({
        title: "Warning",
        text: "Please select a center first",
        icon: "warning",
      });
      return;
    }

    // Check if both fee types are missing
    if (!hasCourseFees && !hasRegistrationFees) {
      showToast({
        title: "Warning",
        text: "This center has no course fees or registration fees attached",
        icon: "warning",
      });
      return;
    }

    setIsGenerating(true);
    setGeneratingType("none");

    // Simulate preparation
    setGenerationStage("preparing");
    setGenerationProgress(20);

    setTimeout(() => {
      // Start with course fee invoices
      setGenerationStage("generating");
      setGeneratingType("course");
      setGenerationProgress(30);

      // Simulate generation progress
      const progressInterval = setInterval(() => {
        setGenerationProgress((prevProgress) => {
          const newProgress = prevProgress + 5;
          if (newProgress >= 50) {
            clearInterval(progressInterval);
            return 50;
          }
          return newProgress;
        });
      }, 300);

      // Actual API calls
      setTimeout(async () => {
        try {
          // Step 1: Generate course fee invoices
          const courseFeeResponse = await generateCourseInvoices({
            center_id: selectedCenterId,
          }).unwrap();

          // Update progress to show we're moving to registration fees
          setGenerationProgress(60);
          setGeneratingType("registration");

          // Step 2: Generate registration fee invoices
          const registrationFeeResponse = await generateRegistrationInvoices({
            center_id: selectedCenterId,
          }).unwrap();

          // Finalization stage
          setGenerationStage("finalizing");
          setGenerationProgress(90);
          setGeneratingType("complete");

          // Reset after showing 100% completion
          setTimeout(() => {
            setGenerationProgress(100);

            setTimeout(() => {
              setIsGenerating(false);
              setGenerationProgress(0);
              setGenerationStage("idle");
              setGeneratingType("none");

              if (
                courseFeeResponse.success ||
                registrationFeeResponse.success
              ) {
                showToast({
                  title: "Success",
                  text: "Invoices generated successfully",
                  icon: "success",
                });
                navigate(-1);
              } else {
                showToast({
                  title: "Warning",
                  text: `Generation completed with issues.`,
                  icon: "warning",
                });
              }
            }, 1000);
          }, 500);
        } catch (error) {
          console.error("Error generating invoices:", error);
          setIsGenerating(false);
          setGenerationProgress(0);
          setGenerationStage("idle");
          setGeneratingType("none");

          showToast({
            title: "Error",
            text: "Failed to generate invoices",
            icon: "error",
          });
        }
      }, 1000);
    }, 800);
  };

  // Get stage display text
  const getStageText = () => {
    switch (generationStage) {
      case "preparing":
        return "Preparing data...";
      case "generating":
        if (generatingType === "course") {
          return "Generating course fee invoices...";
        } else if (generatingType === "registration") {
          return "Generating registration fee invoices...";
        }
        return "Generating invoices...";
      case "finalizing":
        return "Finalizing generation...";
      default:
        return "";
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Generate Invoices</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={getDashboardRoute()}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.collectFees}>Fee Collection</Link>
                </li>
                <li className="breadcrumb-item active">Generate Invoices</li>
              </ol>
            </nav>
          </div>
        </div>
        {/* /Page Header */}

        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-0">Generate Center Invoices</h4>
              </div>
              <div className="card-body">
                {/* Progress or Selection */}
                <div className="mb-4">
                  {isGenerating ? (
                    <div className="generation-progress p-4">
                      <div className="text-center mb-3">
                        <div className="generation-icon mb-3">
                          {generationProgress < 100 ? (
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          ) : (
                            <i className="ti ti-check-circle fs-1 text-success"></i>
                          )}
                        </div>
                        <h5>{getStageText()}</h5>
                        <p className="text-muted mb-0">
                          Please wait while we generate invoices for the
                          selected center
                        </p>
                      </div>

                      <div className="progress mb-2" style={{ height: "10px" }}>
                        <div
                          className={`progress-bar progress-bar-striped progress-bar-animated bg-${
                            generationProgress === 100 ? "success" : "primary"
                          }`}
                          role="progressbar"
                          style={{ width: `${generationProgress}%` }}
                          aria-valuenow={generationProgress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        ></div>
                      </div>

                      <div className="d-flex justify-content-between">
                        <span className="text-muted small">
                          Generating invoices...
                        </span>
                        <span className="fw-medium">{generationProgress}%</span>
                      </div>
                    </div>
                  ) : (
                    <div className="center-selection p-4">
                      <div className="row">
                        <div className="col-md-12 mb-3">
                          {userRole === "centeradmin" ? (
                            <>
                              <p>
                                Click on the button below to generate invoices
                                for the students of your center.
                              </p>
                              {!hasCourseFees && !hasRegistrationFees ? (
                                <div className="mb-3">
                                  <div className="alert alert-warning py-3">
                                    <div className="d-flex align-items-center">
                                      <i className="ti ti-alert-triangle me-2"></i>
                                      <span className="fw-medium">
                                        No fees attached to your center
                                      </span>
                                    </div>
                                  </div>
                                  <div className="d-flex mt-2 justify-content-end">
                                    {isCenterAdmin ? (
                                      <div className="alert alert-info py-2 mb-0 d-flex align-items-center">
                                        <i className="ti ti-phone-call me-2 fs-5"></i>
                                        <div>
                                          <span className="fw-medium">
                                            Action Required:{" "}
                                          </span>
                                          Please contact the main center
                                          administrator to configure fees for
                                          your center
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <Link
                                          to={all_routes.feesConfiguration}
                                          className="btn btn-primary me-2"
                                        >
                                          <i className="ti ti-settings me-1"></i>
                                          Configure Course Fees
                                        </Link>
                                        <Link
                                          to={all_routes.courseRegistrationFee}
                                          className="btn btn-outline-primary"
                                        >
                                          <i className="ti ti-settings me-1"></i>
                                          Configure Registration Fees
                                        </Link>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {!hasCourseFees && (
                                    <div className="mb-3">
                                      <div className="alert alert-warning py-3">
                                        <div className="d-flex align-items-center">
                                          <i className="ti ti-alert-triangle me-2"></i>
                                          <span className="fw-medium">
                                            Course fees not attached to your
                                            center
                                          </span>
                                        </div>
                                      </div>
                                      <div className="d-flex mt-2 justify-content-end">
                                        {isCenterAdmin ? (
                                          <div className="alert alert-info py-2 mb-0 d-flex align-items-center">
                                            <i className="ti ti-phone-call me-2 fs-5"></i>
                                            <div>
                                              <span className="fw-medium">
                                                Action Required:{" "}
                                              </span>
                                              Please contact the main center
                                              administrator to configure fees
                                              for your center
                                            </div>
                                          </div>
                                        ) : (
                                          <Link
                                            to={all_routes.feesConfiguration}
                                            className="btn btn-primary"
                                          >
                                            <i className="ti ti-settings me-1"></i>
                                            Configure Fees
                                          </Link>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {!hasRegistrationFees && (
                                    <div className="mb-3">
                                      <div className="alert alert-warning py-3">
                                        <div className="d-flex align-items-center">
                                          <i className="ti ti-alert-triangle me-2"></i>
                                          <span className="fw-medium">
                                            Registration fees not attached to
                                            your center
                                          </span>
                                        </div>
                                      </div>
                                      <div className="d-flex mt-2 justify-content-end">
                                        {isCenterAdmin ? (
                                          <div className="alert alert-info py-2 mb-0 d-flex align-items-center">
                                            <i className="ti ti-phone-call me-2 fs-5"></i>
                                            <div>
                                              <span className="fw-medium">
                                                Action Required:{" "}
                                              </span>
                                              Please contact the main center
                                              administrator to configure fees
                                              for your center
                                            </div>
                                          </div>
                                        ) : (
                                          <Link
                                            to={
                                              all_routes.courseRegistrationFee
                                            }
                                            className="btn btn-primary"
                                          >
                                            <i className="ti ti-settings me-1"></i>
                                            Configure Fees
                                          </Link>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <label className="form-label">
                                Select Center{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <select
                                className="form-select"
                                value={selectedCenterId}
                                onChange={handleCenterChange}
                                disabled={centersLoading}
                              >
                                <option value="">-- Select a Center --</option>
                                {centers.map((center) => (
                                  <option key={center.id} value={center.id}>
                                    {center.name}
                                  </option>
                                ))}
                              </select>
                              <small className="form-text text-muted">
                                Select the center for which you want to generate
                                invoices
                              </small>
                            </>
                          )}
                        </div>

                        {/* Fee warnings for superadmin */}
                        {selectedCenterId && userRole !== "centeradmin" && (
                          <div className="col-md-12">
                            {!hasCourseFees && !hasRegistrationFees ? (
                              <div className="mb-3">
                                <div className="alert alert-warning py-3">
                                  <div className="d-flex align-items-center">
                                    <i className="ti ti-alert-triangle me-2"></i>
                                    <span className="fw-medium">
                                      No fees attached to this center
                                    </span>
                                  </div>
                                </div>
                                <div className="d-flex mt-2 justify-content-end">
                                  <Link
                                    to={all_routes.feesConfiguration}
                                    className="btn btn-primary me-2"
                                  >
                                    <i className="ti ti-settings me-1"></i>
                                    Configure Course Fees
                                  </Link>
                                  <Link
                                    to={all_routes.courseRegistrationFee}
                                    className="btn btn-outline-primary"
                                  >
                                    <i className="ti ti-settings me-1"></i>
                                    Configure Registration Fees
                                  </Link>
                                </div>
                              </div>
                            ) : (
                              <>
                                {!hasCourseFees && (
                                  <div className="mb-3">
                                    <div className="alert alert-warning py-3">
                                      <div className="d-flex align-items-center">
                                        <i className="ti ti-alert-triangle me-2"></i>
                                        <span className="fw-medium">
                                          Course fees not attached to this
                                          center
                                        </span>
                                      </div>
                                    </div>
                                    <div className="d-flex mt-2 justify-content-end">
                                      <Link
                                        to={all_routes.feesConfiguration}
                                        className="btn btn-primary"
                                      >
                                        <i className="ti ti-settings me-1"></i>
                                        Configure Fees
                                      </Link>
                                    </div>
                                  </div>
                                )}
                                {!hasRegistrationFees && (
                                  <div className="mb-3">
                                    <div className="alert alert-warning py-3">
                                      <div className="d-flex align-items-center">
                                        <i className="ti ti-alert-triangle me-2"></i>
                                        <span className="fw-medium">
                                          Registration fees not attached to this
                                          center
                                        </span>
                                      </div>
                                    </div>
                                    <div className="d-flex mt-2 justify-content-end">
                                      <Link
                                        to={all_routes.courseRegistrationFee}
                                        className="btn btn-primary"
                                      >
                                        <i className="ti ti-settings me-1"></i>
                                        Configure Fees
                                      </Link>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Instructions */}
                <div className="generate-instructions bg-light-200 p-3 rounded-3">
                  <h6 className="mb-3">Important Information:</h6>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">
                      <i className="ti ti-point text-primary me-2"></i>
                      Both course fee and registration fee invoices will be
                      generated for eligible students
                    </li>
                    <li className="mb-2">
                      <i className="ti ti-point text-primary me-2"></i>
                      The process might take a few minutes for centers with many
                      students
                    </li>
                    <li className="mb-2">
                      <i className="ti ti-point text-primary me-2"></i>
                      Students with existing invoices for the current period
                      will be skipped
                    </li>
                    <li>
                      <i className="ti ti-point text-primary me-2"></i>
                      Invoices will be automatically set with a 30-day due date
                    </li>
                  </ul>
                </div>
              </div>
              <div className="card-footer text-end">
                <button
                  onClick={() => navigate(-1)}
                  className="btn btn-light me-2"
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={
                    !selectedCenterId ||
                    isGenerating ||
                    centersLoading ||
                    isSelectedCenterLoading ||
                    (isCenterAdmin && centerLoading) ||
                    !hasCourseFees ||
                    !hasRegistrationFees
                  }
                  onClick={handleGenerateInvoices}
                  title={
                    !hasCourseFees && !hasRegistrationFees
                      ? "No fees attached to this center"
                      : ""
                  }
                >
                  {isGenerating ? "Processing..." : "Generate Invoices"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateInvoices;
